import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api } from "@/lib/api";

const roleHome = { parent: "/dashboard/parent", tutor: "/dashboard/tutor", student: "/dashboard/student", admin: "/dashboard/tutor" };

export default function Auth() {
  const [params] = useSearchParams();
  const { login, register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "parent" });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const sendForgot = async () => {
    if (!forgotEmail) return toast.error("Enter your email");
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If that email exists, a reset link is on its way.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch {
      toast.success("If that email exists, a reset link is on its way.");
      setForgotOpen(false);
    } finally {
      setForgotLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (mode === "register") u = await register(form);
      else u = await login(form.email, form.password);
      toast.success(mode === "register" ? "Welcome to Up Smart Learning!" : "Welcome back!");
      navigate(roleHome[u.role] || "/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { v: "parent", t: "Parent", d: "Book & pay for lessons" },
    { v: "student", t: "Student", d: "Join your lessons" },
    { v: "tutor", t: "Tutor", d: "Teach & schedule" },
  ];

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between bg-[#595C2B] text-white p-12 grain">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-brand">
          <img src="/logo_mark.png" alt="Up Smart Learning logo" className="h-9 w-9 object-contain" />
          <span className="font-display text-2xl font-bold">Up Smart Learning</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight">Where UK students<br />earn top grades.</h2>
          <p className="text-white/70 mt-4 max-w-sm">Expert GCSE & A-Level tuition, live on Google Meet. Booking, payments and progress in one elegant dashboard.</p>
        </div>
        <p className="text-white/40 text-sm">© {new Date().getFullYear()} Up Smart Learning Ltd</p>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#FBFBFA]">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold text-[#595C2B]">{mode === "register" ? "Create your account" : "Welcome back"}</h1>
          <p className="text-slate-500 mt-1 mb-6">{mode === "register" ? "Start booking expert lessons today." : "Log in to your dashboard."}</p>

          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="grid grid-cols-2 w-full rounded-full">
              <TabsTrigger value="login" className="rounded-full" data-testid="tab-login">Log in</TabsTrigger>
              <TabsTrigger value="register" className="rounded-full" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-sm font-medium">Full name</Label>
                  <Input value={form.name} onChange={upd("name")} required placeholder="Jane Smith" className="mt-1" data-testid="register-name-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">I am a...</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {roles.map((r) => (
                      <button type="button" key={r.v} onClick={() => setForm({ ...form, role: r.v })}
                        data-testid={`role-${r.v}`}
                        className={`rounded-xl border p-3 text-left btn-lift ${form.role === r.v ? "border-[#595C2B] bg-[#595C2B] text-white" : "border-slate-200 bg-white"}`}>
                        <span className="block text-sm font-semibold">{r.t}</span>
                        <span className={`block text-[11px] mt-0.5 ${form.role === r.v ? "text-white/60" : "text-slate-400"}`}>{r.d}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input type="email" value={form.email} onChange={upd("email")} required placeholder="you@example.com" className="mt-1" data-testid="email-input" />
            </div>
            <div>
              <Label className="text-sm font-medium">Password</Label>
              <Input type="password" value={form.password} onChange={upd("password")} required placeholder="••••••••" className="mt-1" data-testid="password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#595C2B] hover:bg-[#45481F] text-white btn-lift h-11" data-testid="auth-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "register" ? "Create account" : "Log in"}
            </Button>
            {mode === "login" && (
              <button type="button" onClick={() => setForgotOpen(true)} className="w-full text-center text-sm text-[#595C2B] hover:underline mt-1" data-testid="forgot-password-link">
                Forgot your password?
              </button>
            )}
          </form>

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent data-testid="forgot-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#595C2B]">Reset your password</DialogTitle>
                <DialogDescription>Enter your account email and we'll send you a reset link.</DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <Label className="text-sm font-medium">Email</Label>
                <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" className="mt-1" data-testid="forgot-email-input" />
              </div>
              <DialogFooter>
                <Button onClick={sendForgot} disabled={forgotLoading} className="w-full rounded-full bg-[#595C2B] hover:bg-[#45481F] text-white" data-testid="forgot-submit-btn">
                  {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    </div>
  );
}
